import React, { useState, useEffect } from 'react';
import { ArrowUpDown, GripVertical, Plus, X } from 'lucide-react';
import ActivityLayout, {
    Toggle, SettingsGroup, SettingsRow,
    AIButton, AIUndoButton, PollResults, AddImageSection, QuestionImage,
} from './ActivityLayout';
import Tooltip from '../../components/ui/Tooltip';
import styles from './ActivityLayout.module.css';
import useActivityContent from '../../hooks/useActivityContent';
import ParticipantExperience from '../join/ParticipantExperience';
import { API_PATH, request } from '../../config/api';
import { useConfirm } from '../../store/ConfirmDialogContext';

const DUMMY_DATA = {
    question: 'Arrange the planets from closest to farthest from the Sun 🌍',
    items: ['Mercury', 'Venus', 'Earth', 'Mars', 'Jupiter', 'Saturn'],
    settings: { description: true, descText: 'Drag and drop the items to arrange them in the correct order.', pollResults: 'hidden', isLocked: false, allowUpdateResponse: true, imageUrl: null },
};

const CorrectOrder = ({
    event, activity, isLaunched, onLaunch, onStop,
    onNext, onPrev, hasNext, hasPrev, onSettingsUpdated
}) => {
    const confirm = useConfirm();
    const [question, setQuestion] = useState('');
    const [editingQuestion, setEditingQuestion] = useState(false);
    const [editingDesc, setEditingDesc] = useState(false);
    const [items, setItems] = useState(['Item 1', 'Item 2', 'Item 3']);
    const [settings, setSettings] = useState({
        description: false,
        descText: '',
        pollResults: 'hidden',
        isLocked: false,
        allowUpdateResponse: true,
        imageUrl: null,
    });

    const { content, isLoading, isSaving, saveStatus, saveContent } =
        useActivityContent(event?.id, activity?.id);

    const [isImproving, setIsImproving] = useState(false);
    const [isImprovingDesc, setIsImprovingDesc] = useState(false);
    const [previousQuestion, setPreviousQuestion] = useState(null);
    const [previousDescText, setPreviousDescText] = useState(null);
    const API_BASE = API_PATH;

    const handleImproveWithAI = async () => {
        if (!question.trim()) return;
        setIsImproving(true);
        try {
            const res = await request(`${API_BASE}/ai/suggest`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ type: 'improve', text: question }),
            });
            if (res.ok) {
                const data = await res.json();
                if (data.suggestion) {
                    setPreviousQuestion(question);
                    setQuestion(data.suggestion);
                }
            }
        } catch (err) {
            console.error(err);
        } finally {
            setIsImproving(false);
        }
    };

    const handleUndoQuestion = () => {
        if (previousQuestion !== null) {
            setQuestion(previousQuestion);
            setPreviousQuestion(null);
        }
    };

    const handleImproveDescWithAI = async () => {
        const text = settings.descText?.trim() || '';
        if (!text) return;
        setIsImprovingDesc(true);
        try {
            const res = await request(`${API_BASE}/ai/suggest`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ type: 'improve', text }),
            });
            if (res.ok) {
                const data = await res.json();
                if (data.suggestion) {
                    setPreviousDescText(text);
                    set('descText', data.suggestion);
                }
            }
        } catch (err) {
            console.error(err);
        } finally {
            setIsImprovingDesc(false);
        }
    };

    const handleUndoDesc = () => {
        if (previousDescText !== null) {
            set('descText', previousDescText);
            setPreviousDescText(null);
        }
    };

    useEffect(() => {
        if (!content || Object.keys(content).length === 0) return;
        if (content.question !== undefined) setQuestion(content.question);
        if (content.items) setItems(content.items);
        if (content.settings) setSettings(s => ({ ...s, ...content.settings }));
    }, [content]);

    const set = (k, v) => setSettings((s) => ({ ...s, [k]: v }));
    const openQuestionEditor = () => setEditingQuestion(true);
    const closeQuestionEditor = () => setEditingQuestion(false);
    const openDescEditor = () => setEditingDesc(true);
    const closeDescEditor = () => setEditingDesc(false);
    const handleKeyToEdit = (e, openFn) => {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openFn(); }
    };
    const handleEditKeyDown = (e, closeFn) => {
        if (e.key === 'Enter' || e.key === 'Escape') { e.preventDefault(); closeFn(); }
    };
    const addItem = () => setItems([...items, '']);
    const removeItem = async (i) => {
        const ok = await confirm({
            title: 'Delete Item',
            message: 'Delete this item?',
            confirmText: 'Delete',
            tone: 'danger',
        });
        if (!ok) return;
        setItems(items.filter((_, j) => j !== i));
    };
    const updateItem = (i, v) => setItems(items.map((o, j) => (j === i ? v : o)));

    const [dragItemIndex, setDragItemIndex] = useState(null);
    const [dragOverItemIndex, setDragOverItemIndex] = useState(null);
    const handleItemDragStart = (e, i) => {
        setDragItemIndex(i);
        e.dataTransfer.effectAllowed = 'move';
        e.dataTransfer.setData('text/plain', String(i));
    };
    const handleItemDragOver = (e, i) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        if (dragItemIndex !== null && dragItemIndex !== i) setDragOverItemIndex(i);
    };
    const handleItemDragLeave = () => setDragOverItemIndex(null);
    const handleItemDrop = (e, dropIndex) => {
        e.preventDefault();
        setDragOverItemIndex(null);
        if (dragItemIndex == null || dragItemIndex === dropIndex) {
            setDragItemIndex(null);
            return;
        }
        const next = [...items];
        const [removed] = next.splice(dragItemIndex, 1);
        next.splice(dropIndex, 0, removed);
        setItems(next);
        setDragItemIndex(null);
    };
    const handleItemDragEnd = () => { setDragItemIndex(null); setDragOverItemIndex(null); };

    const right = (
        <>
            <SettingsGroup title="Poll Settings">
                <SettingsRow label="Poll description" description="Add description visible to participants">
                    <Toggle checked={settings.description} onChange={(e) => set('description', e.target.checked)} />
                </SettingsRow>
            </SettingsGroup>

            <SettingsGroup title="Poll Results">
                <PollResults value={settings.pollResults} onChange={(v) => set('pollResults', v)} />
                <SettingsRow label="Allow participants to update response" description="Let participants change their answer after submitting">
                    <Toggle checked={settings.allowUpdateResponse !== false} onChange={(e) => set('allowUpdateResponse', e.target.checked)} />
                </SettingsRow>
            </SettingsGroup>

            <SettingsGroup>
                <AddImageSection
                    imageUrl={settings.imageUrl}
                    onImageChange={(url) => set('imageUrl', url)}
                />
            </SettingsGroup>
        </>
    );

    const left = (
        <>
            <div className={`${styles.questionCard} ${settings.imageUrl ? styles.questionCardWithImage : ''}`}>
                {settings.imageUrl && (
                    <QuestionImage imageUrl={settings.imageUrl} onImageChange={(url) => set('imageUrl', url)} />
                )}
                <div className={styles.questionContent}>
                    <div className={styles.questionLabelRow}>
                        <span className={styles.questionLabel}>Question <span className={styles.requiredStar}>*</span></span>
                        <span className={styles.aiActions}>
                            <AIButton onClick={handleImproveWithAI} loading={isImproving} />
                            {previousQuestion !== null && (
                                <AIUndoButton onClick={handleUndoQuestion} label="Undo" />
                            )}
                        </span>
                    </div>
                    {editingQuestion ? (
                        <input
                            className={styles.questionInput}
                            value={question}
                            onChange={(e) => setQuestion(e.target.value)}
                            placeholder="Arrange the following in the correct order..."
                            autoFocus
                            onBlur={closeQuestionEditor}
                            onKeyDown={(e) => handleEditKeyDown(e, closeQuestionEditor)}
                        />
                    ) : (
                        <Tooltip title="Click to edit question" placement="top">
                            <div
                                className={`${styles.questionTextDisplay} ${!question ? styles.questionTextPlaceholder : ''}`}
                                role="button"
                                tabIndex={0}
                                onClick={openQuestionEditor}
                                onKeyDown={(e) => handleKeyToEdit(e, openQuestionEditor)}
                            >
                                {question || 'Type your question here...'}
                            </div>
                        </Tooltip>
                    )}

                    {settings.description && (
                        <div className={styles.questionDescription}>
                            <div className={styles.questionLabelRow}>
                                <span className={styles.descLabel}>Description</span>
                                <span className={styles.aiActions}>
                                    <AIButton onClick={handleImproveDescWithAI} loading={isImprovingDesc} />
                                    {previousDescText !== null && (
                                        <AIUndoButton onClick={handleUndoDesc} label="Undo" />
                                    )}
                                </span>
                            </div>
                            {editingDesc ? (
                                <input
                                    className={styles.descInput}
                                    value={settings.descText}
                                    onChange={(e) => set('descText', e.target.value)}
                                    placeholder="Type description here..."
                                    autoFocus
                                    onBlur={closeDescEditor}
                                    onKeyDown={(e) => handleEditKeyDown(e, closeDescEditor)}
                                />
                            ) : (
                                <Tooltip title="Click to edit description" placement="top">
                                    <div
                                        className={`${styles.descTextDisplay} ${!settings.descText ? styles.descTextPlaceholder : ''}`}
                                        role="button"
                                        tabIndex={0}
                                        onClick={openDescEditor}
                                        onKeyDown={(e) => handleKeyToEdit(e, openDescEditor)}
                                    >
                                        {settings.descText || 'Type description here...'}
                                    </div>
                                </Tooltip>
                            )}
                        </div>
                    )}
                </div>
            </div>

            {/* How it works */}
            <div className={`${styles.infoCard} ${styles.infoCardLime}`}>
                <div className={styles.infoCardLabel}>How Correct Order works</div>
                <p className={styles.infoCardText}>
                    <strong>You (admin):</strong> Write the question and add items below in the <strong>correct order</strong> (top = first, bottom = last). Drag rows to reorder. This order is the answer key.
                    <br />
                    <strong>Participants:</strong> See the same items in a random order and drag them into the correct sequence, then submit. Results show who got the order right.
                </p>
            </div>

            {/* Items — drag to set correct order */}
            <div className={styles.optionsCard}>
                <div className={styles.optionsLabel}>Items (drag to set correct order — top to bottom = answer key)</div>
                {items.map((item, i) => (
                    <div
                        key={i}
                        className={`${styles.optionRow} ${styles.correctOrderEditorRow} ${dragItemIndex === i ? styles.correctOrderEditorRowDragging : ''} ${dragOverItemIndex === i ? styles.correctOrderEditorRowDragOver : ''}`}
                        draggable
                        onDragStart={(e) => handleItemDragStart(e, i)}
                        onDragOver={(e) => handleItemDragOver(e, i)}
                        onDragLeave={handleItemDragLeave}
                        onDrop={(e) => handleItemDrop(e, i)}
                        onDragEnd={handleItemDragEnd}
                    >
                        <span className={styles.correctOrderEditorGrip} aria-hidden>
                            <GripVertical size={16} />
                        </span>
                        <span className={styles.optionNumber}>{i + 1}</span>
                        <input
                            className={styles.optionInput}
                            value={item}
                            onChange={(e) => updateItem(i, e.target.value)}
                            placeholder={`Item ${i + 1}`}
                            onClick={(e) => e.stopPropagation()}
                        />
                        {items.length > 2 && (
                            <button type="button" className={styles.optionRemoveBtn} onClick={(e) => { e.stopPropagation(); removeItem(i); }} aria-label="Remove item">
                                <X size={14} />
                            </button>
                        )}
                    </div>
                ))}
                <div className={styles.optionsFooter}>
                    <button type="button" className={styles.addOptionBtn} onClick={addItem}>
                        <Plus size={14} /> Add item
                    </button>
                </div>
            </div>
        </>
    );

    const loadDummy = () => {
        setQuestion(DUMMY_DATA.question);
        setItems(DUMMY_DATA.items);
        setSettings(s => ({ ...s, ...DUMMY_DATA.settings }));
    };

    const participantPreview = (
        <ParticipantExperience
            step="activity"
            name="Preview User"
            event={event}
            activity={{
                type: 'correct-order',
                title: question || 'Correct Order',
                content: { question, items, settings }
            }}
        />
    );

    return (
        <ActivityLayout
            icon={<ArrowUpDown size={20} />}
            color="var(--cat-teal)"
            title="Correct Order"
            eventName={event?.name}
            event={event}
            activity={activity}
            left={left}
            right={right}
            participantPreview={participantPreview}
            validate={() => !question.trim() ? 'Question is required.' : null}
            onSave={() => {
                setPreviousQuestion(null);
                setPreviousDescText(null);
                saveContent({ question, items, settings });
            }}
            onLoadExample={loadDummy}
            isSaving={isSaving}
            saveStatus={saveStatus}
            isLoading={isLoading}
            isLaunched={isLaunched}
            onLaunch={onLaunch}
            onStop={onStop}
            onNext={onNext}
            onPrev={onPrev}
            hasNext={hasNext}
            hasPrev={hasPrev}
            onSettingsUpdated={onSettingsUpdated}
        />
    );
};

export default CorrectOrder;
